<script setup>
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import AdvancedSearchCriteria from '../advanced_search/AdvancedSearchCriteria.vue';

  // The other props (the criteria options and initial values) go through to the criteria
  defineOptions({ inheritAttrs: false });

  const { t } = useI18n();

  const props = defineProps({
    initialSort: { type: String, default: '' },
    initialReleaseDate: { type: String, default: '' },
    nextStepTarget: { type: String, default: '#second_step' }
  });

  const SORT_OPTIONS = [
    { value: 'best_sellers', labelKey: 'scheduled_cart_filters.sort_best_sellers' },
    { value: 'most_borrowed', labelKey: 'scheduled_cart_filters.sort_most_borrowed' },
    { value: 'random', labelKey: 'scheduled_cart_filters.sort_random' },
    { value: 'promotions', labelKey: 'scheduled_cart_filters.sort_promotions' },
    { value: 'recent', labelKey: 'scheduled_cart_filters.sort_recent' }
  ];
  // The publication period only goes with the latest releases, rather than among the criteria
  const RELEASE_DATE_OPTIONS = [
    { value: 'recent_week', labelKey: 'advanced_search.date_range.recent_week' },
    { value: 'recent_month', labelKey: 'advanced_search.date_range.recent_month' },
    { value: 'recent_year', labelKey: 'advanced_search.date_range.recent_year' }
  ];

  const sort = ref(props.initialSort);
  const release_date = ref(props.initialReleaseDate);
  watch(sort, (value) => { if (value !== 'recent') release_date.value = ''; });
  const criteria = ref(null);

  const isValid = computed(() => criteria.value?.narrowed);
</script>

<template>
  <div class="accordion-item">
    <h2 class="accordion-header">
      <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#metadata_step" aria-expanded="true" aria-controls="metadata_step">
        {{ t('scheduled_cart_filters.metadata_step_title') }}
      </button>
    </h2>
    <div id="metadata_step" class="accordion-collapse collapse show" data-bs-parent="#scheduled_cart_steps">
      <div class="accordion-body">
        <AdvancedSearchCriteria ref="criteria" v-bind="$attrs" input_name="search_criteria" />

        <div class="row mt-3">
          <div class="col">
            <small v-if="!isValid" class="text-muted">{{ t('scheduled_cart_filters.metadata_required_hint') }}</small>
          </div>
          <div class="col d-flex justify-content-end">
            <button
              type="button"
              class="btn mkt-btn-primary"
              :disabled="!isValid"
              data-bs-toggle="collapse"
              data-bs-target="#search_criteria_step"
              aria-expanded="false"
              aria-controls="search_criteria_step"
            >
              {{ t('scheduled_cart_filters.next_step') }}<i class="bi bi-arrow-right ms-1" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="accordion-item">
    <h2 class="accordion-header">
      <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#search_criteria_step" aria-expanded="false" aria-controls="search_criteria_step">
        {{ t('scheduled_cart_filters.search_criteria_step_title') }}
      </button>
    </h2>
    <div id="search_criteria_step" class="accordion-collapse collapse" data-bs-parent="#scheduled_cart_steps">
      <div class="accordion-body">
        <fieldset>
          <div class="form-check" v-for="opt in SORT_OPTIONS" :key="opt.value">
            <input type="radio" class="form-check-input" :id="`sort_${opt.value}`" name="context_overrides[sort]" :value="opt.value" v-model="sort" />
            <label class="form-check-label" :for="`sort_${opt.value}`">{{ t(opt.labelKey) }}</label>
            <fieldset v-if="opt.value === 'recent' && sort === 'recent'" class="mt-2">
              <legend class="sr-only">{{ t('scheduled_cart_filters.period_title') }}</legend>
              <div class="form-check" v-for="period in RELEASE_DATE_OPTIONS" :key="period.value">
                <input type="radio" class="form-check-input" :id="`release_date_${period.value}`" name="context_overrides[release_date]" :value="period.value" v-model="release_date" />
                <label class="form-check-label" :for="`release_date_${period.value}`">{{ t(period.labelKey) }}</label>
              </div>
            </fieldset>
          </div>
        </fieldset>

        <div class="row mt-3">
          <div class="col d-flex justify-content-end">
            <button
              type="button"
              class="btn mkt-btn-primary"
              :disabled="!isValid"
              data-bs-toggle="collapse"
              :data-bs-target="nextStepTarget"
              :aria-expanded="false"
            >
              {{ t('scheduled_cart_filters.next_step') }}<i class="bi bi-arrow-right ms-1" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
<i18n src="../../locales/de.json" global></i18n>
